import React, { useState, useRef, useEffect, useCallback } from "react";
import { INSTRUMENTS, fmt } from "./constants";

// ─── TRADE REPLAY ─────────────────────────────────────────────
// Replays a past trading session bar-by-bar. Uses simulated OHLCV data
// matching the candle chart's generation algorithm so candles look identical.


function seedRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function generateSessionBars(symbol, date, tf = "5m") {
  const inst = INSTRUMENTS[symbol] || INSTRUMENTS["ES"];
  const tfMins = { "1m": 1, "2m": 2, "3m": 3, "5m": 5, "10m": 10, "15m": 15, "30m": 30 };
  const mins = tfMins[tf] || 5;
  const barsPerSession = Math.floor(390 / mins); // RTH = 6.5 hours
  const seed = date.getTime() / 1000 + symbol.charCodeAt(0);
  const rng = seedRandom(seed);

  const basePrice = inst.basePrice || 4500;
  const vol = inst.volatility || 0.0008;
  const bars = [];
  let price = basePrice * (0.98 + rng() * 0.04);
  const sessionStart = new Date(date);
  sessionStart.setHours(9, 30, 0, 0);

  for (let i = 0; i < barsPerSession; i++) {
    const ts = sessionStart.getTime() + i * mins * 60000;
    const move = (rng() - 0.5) * price * vol * Math.sqrt(mins);
    const open = price;
    const close = price + move;
    const hi = Math.max(open, close) + rng() * price * vol * 0.5;
    const lo = Math.min(open, close) - rng() * price * vol * 0.5;
    const volume = Math.round(500 + rng() * 2000);
    bars.push({ ts, open, high: hi, low: lo, close, volume });
    price = close;
  }
  return bars;
}

export function TradeReplay({ history = [], symbol = "ES", onClose }) {
  const canvasRef = useRef(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bars, setBars] = useState([]);
  const [currentBar, setCurrentBar] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(4); // bars per second
  const [trades, setTrades] = useState([]); // trades placed during replay
  const [replayPnl, setReplayPnl] = useState(0);
  const [position, setPosition] = useState(null); // { side, entry, qty }
  const [tf, setTf] = useState("5m");
  const animRef = useRef(null);
  const lastTickRef = useRef(0);

  // Build session list from trade history
  useEffect(() => {
    const days = new Set();
    history.forEach(t => {
      if (t.closedAt) {
        const d = new Date(t.closedAt);
        days.add(d.toDateString());
      }
    });
    // Also add last 5 trading days even if no trades
    const today = new Date();
    for (let i = 0; i < 10; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      if (d.getDay() !== 0 && d.getDay() !== 6) days.add(d.toDateString());
      if (days.size >= 7) break;
    }
    const sorted = [...days].sort((a, b) => new Date(b) - new Date(a)).slice(0, 7);
    setSessions(sorted);
    if (sorted.length) setSelectedSession(sorted[0]);
  }, [history]);

  // Load bars when session / tf changes
  useEffect(() => {
    if (!selectedSession) return;
    const d = new Date(selectedSession);
    const b = generateSessionBars(symbol, d, tf);
    setBars(b);
    setCurrentBar(0);
    setIsPlaying(false);
    setTrades([]);
    setReplayPnl(0);
    setPosition(null);
  }, [selectedSession, symbol, tf]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying) { cancelAnimationFrame(animRef.current); return; }
    const tick = (now) => {
      if (now - lastTickRef.current > 1000 / speed) {
        lastTickRef.current = now;
        setCurrentBar(b => {
          if (b >= bars.length - 1) { setIsPlaying(false); return b; }
          return b + 1;
        });
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [isPlaying, speed, bars.length]);

  // Draw chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bars.length) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const visibleBars = bars.slice(0, currentBar + 1);
    const lookback = Math.min(visibleBars.length, 80);
    const displayBars = visibleBars.slice(-lookback);
    if (!displayBars.length) return;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#0d0f14";
    ctx.fillRect(0, 0, W, H);

    const pad = { l: 8, r: 56, t: 16, b: 28 };
    const cW = W - pad.l - pad.r;
    const cH = H - pad.t - pad.b;
    const barW = Math.max(2, cW / lookback - 1);
    const gap = cW / lookback;

    const highs = displayBars.map(b => b.high);
    const lows = displayBars.map(b => b.low);
    const maxP = Math.max(...highs);
    const minP = Math.min(...lows);
    const range = maxP - minP || 1;

    const py = p => pad.t + cH - ((p - minP) / range) * cH;
    const px = i => pad.l + i * gap + gap / 2;

    // Grid
    ctx.strokeStyle = "#1e2130";
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      const y = pad.t + (cH / 4) * i;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke();
      const price = maxP - (range / 4) * i;
      ctx.fillStyle = "#3a3d4a";
      ctx.font = "10px monospace";
      ctx.fillText(price.toFixed(2), W - pad.r + 4, y + 3);
    }

    // Volume bars
    const maxVol = Math.max(...displayBars.map(b => b.volume));
    displayBars.forEach((bar, i) => {
      const x = px(i);
      const volH = (bar.volume / maxVol) * (cH * 0.15);
      ctx.fillStyle = bar.close >= bar.open ? "rgba(0,200,150,0.15)" : "rgba(240,59,59,0.15)";
      ctx.fillRect(x - barW / 2, pad.t + cH - volH, barW, volH);
    });

    // Candles
    displayBars.forEach((bar, i) => {
      const x = px(i);
      const isUp = bar.close >= bar.open;
      const color = isUp ? "#00c896" : "#f03b3b";
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 1;

      // Wick
      ctx.beginPath();
      ctx.moveTo(x, py(bar.high));
      ctx.lineTo(x, py(bar.low));
      ctx.stroke();

      // Body
      const bodyTop = py(Math.max(bar.open, bar.close));
      const bodyH = Math.max(1, Math.abs(py(bar.open) - py(bar.close)));
      if (isUp) {
        ctx.fillRect(x - barW / 2, bodyTop, barW, bodyH);
      } else {
        ctx.strokeRect(x - barW / 2, bodyTop, barW, bodyH);
      }
    });

    // Trade markers
    trades.forEach(t => {
      const tBar = bars.findIndex(b => b.ts >= t.ts);
      const dispIdx = displayBars.findIndex(b => b.ts === bars[tBar]?.ts);
      if (dispIdx < 0) return;
      const x = px(dispIdx);
      const y = py(t.price);
      ctx.fillStyle = t.side === "buy" ? "#00c896" : "#f03b3b";
      ctx.beginPath();
      ctx.moveTo(x, y + (t.side === "buy" ? 10 : -10));
      ctx.lineTo(x - 5, y + (t.side === "buy" ? 18 : -18));
      ctx.lineTo(x + 5, y + (t.side === "buy" ? 18 : -18));
      ctx.closePath();
      ctx.fill();
    });

    // Current price line
    if (displayBars.length) {
      const lastBar = displayBars[displayBars.length - 1];
      const y = py(lastBar.close);
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#1a1d27";
      ctx.fillRect(W - pad.r, y - 9, pad.r - 2, 18);
      ctx.strokeStyle = "#3a3d4a";
      ctx.strokeRect(W - pad.r, y - 9, pad.r - 2, 18);
      ctx.fillStyle = "#e2e4e9";
      ctx.font = "bold 10px monospace";
      ctx.fillText(lastBar.close.toFixed(2), W - pad.r + 3, y + 4);
    }

    // Time axis
    if (displayBars.length > 1) {
      const step = Math.ceil(displayBars.length / 6);
      ctx.fillStyle = "#3a3d4a";
      ctx.font = "9px monospace";
      displayBars.forEach((bar, i) => {
        if (i % step === 0) {
          ctx.fillText(fmt.time(bar.ts), px(i) - 16, H - 6);
        }
      });
    }
  }, [bars, currentBar, trades]);

  const currentPrice = bars[currentBar]?.close || 0;

  const handleTrade = (side) => {
    if (!bars[currentBar]) return;
    const price = bars[currentBar].close;
    const qty = 1;
    if (position) {
      // Close position
      const pnl = (side === "buy" ? -1 : 1) *
        (price - position.entry) * qty * (position.side === "long" ? 1 : -1) *
        (INSTRUMENTS[symbol]?.tickVal / INSTRUMENTS[symbol]?.tickSize || 50);
      setReplayPnl(p => p + pnl);
      setTrades(t => [...t, { side, price, ts: bars[currentBar].ts, close: true, pnl }]);
      setPosition(null);
    } else {
      setPosition({ side: side === "buy" ? "long" : "short", entry: price, qty });
      setTrades(t => [...t, { side, price, ts: bars[currentBar].ts }]);
    }
  };

  const unrealizedPnl = position && currentPrice
    ? (currentPrice - position.entry) *
      (position.side === "long" ? 1 : -1) *
      (INSTRUMENTS[symbol]?.tickVal / INSTRUMENTS[symbol]?.tickSize || 50)
    : 0;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        width: 900, height: 620, background: "#0d0f14",
        border: "1px solid #2a2d3a", borderRadius: 10,
        display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,0,0,0.9)",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ height: 44, display: "flex", alignItems: "center", padding: "0 16px", borderBottom: "1px solid #1e2130", gap: 12, flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#e2e4e9", letterSpacing: "0.05em" }}>▶ TRADE REPLAY</span>
          <span style={{ fontSize: 11, color: "#4a4f5e" }}>{symbol}</span>

          {/* Session selector */}
          <select value={selectedSession || ""} onChange={e => setSelectedSession(e.target.value)}
            style={{ background: "#1a1d27", border: "1px solid #2a2d3a", color: "#a0a5b4", fontSize: 10, padding: "3px 6px", borderRadius: 3 }}>
            {sessions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Timeframe */}
          {["1m", "3m", "5m", "15m", "30m"].map(t => (
            <button key={t} onClick={() => setTf(t)} style={{
              padding: "3px 7px", fontSize: 10, fontWeight: 600,
              background: tf === t ? "rgba(0,200,150,0.12)" : "transparent",
              border: `1px solid ${tf === t ? "rgba(0,200,150,0.4)" : "#2a2d3a"}`,
              color: tf === t ? "#00c896" : "#5a5f6e", borderRadius: 3, cursor: "pointer",
            }}>{t}</button>
          ))}

          <div style={{ flex: 1 }} />

          {/* Speed */}
          <span style={{ fontSize: 9, color: "#4a4f5e" }}>Speed</span>
          <input type="range" min={1} max={20} value={speed} onChange={e => setSpeed(+e.target.value)}
            style={{ width: 80, accentColor: "#00c896" }} />
          <span style={{ fontSize: 10, fontFamily: "monospace", color: "#7a7f8e", width: 28 }}>{speed}×</span>

          <button onClick={onClose} style={{ background: "none", border: "none", color: "#4a4f5e", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Chart */}
          <div style={{ flex: 1, position: "relative", background: "#0d0f14" }}>
            <canvas ref={canvasRef} width={700} height={480}
              style={{ width: "100%", height: "100%", display: "block" }} />

            {/* Progress bar */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#1e2130" }}>
              <div style={{ height: "100%", width: `${bars.length ? (currentBar / bars.length) * 100 : 0}%`, background: "#00c896", transition: "width 0.1s" }} />
            </div>
          </div>

          {/* Controls panel */}
          <div style={{ width: 180, borderLeft: "1px solid #1e2130", display: "flex", flexDirection: "column", padding: 12, gap: 10 }}>

            {/* P&L */}
            <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 4, padding: "8px 10px" }}>
              <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Replay P&L</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "monospace", color: replayPnl + unrealizedPnl >= 0 ? "#00c896" : "#f03b3b" }}>
                {(replayPnl + unrealizedPnl) >= 0 ? "+" : ""}${(replayPnl + unrealizedPnl).toFixed(2)}
              </div>
              {position && (
                <div style={{ fontSize: 10, color: "#5a5f6e", marginTop: 4 }}>
                  {position.side.toUpperCase()} @ {position.entry.toFixed(2)}
                  <span style={{ color: unrealizedPnl >= 0 ? "#00c896" : "#f03b3b", marginLeft: 6 }}>
                    {unrealizedPnl >= 0 ? "+" : ""}${unrealizedPnl.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Bar info */}
            <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 4, padding: "8px 10px" }}>
              <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Current Bar</div>
              {bars[currentBar] && (
                <>
                  <div style={{ fontSize: 10, color: "#7a7f8e", marginBottom: 2 }}>{fmt.time(bars[currentBar].ts)}</div>
                  {[
                    ["O", bars[currentBar].open],
                    ["H", bars[currentBar].high],
                    ["L", bars[currentBar].low],
                    ["C", bars[currentBar].close],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 1 }}>
                      <span style={{ fontSize: 10, color: "#4a4f5e" }}>{label}</span>
                      <span style={{ fontSize: 10, fontFamily: "monospace", color: label === "C" ? (bars[currentBar].close >= bars[currentBar].open ? "#00c896" : "#f03b3b") : "#a0a5b4" }}>{val.toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: 9, color: "#4a4f5e" }}>Bar</span>
                    <span style={{ fontSize: 9, fontFamily: "monospace", color: "#5a5f6e" }}>{currentBar + 1}/{bars.length}</span>
                  </div>
                </>
              )}
            </div>

            {/* Trade buttons */}
            <button onClick={() => handleTrade("buy")} style={{
              padding: "9px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 4,
              background: position?.side === "long" ? "rgba(240,59,59,0.15)" : "rgba(0,200,150,0.15)",
              border: `1px solid ${position?.side === "long" ? "rgba(240,59,59,0.4)" : "rgba(0,200,150,0.4)"}`,
              color: position?.side === "long" ? "#f03b3b" : "#00c896",
            }}>
              {position?.side === "long" ? "CLOSE LONG" : "BUY LONG"}
            </button>
            <button onClick={() => handleTrade("sell")} style={{
              padding: "9px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 4,
              background: position?.side === "short" ? "rgba(0,200,150,0.15)" : "rgba(240,59,59,0.15)",
              border: `1px solid ${position?.side === "short" ? "rgba(0,200,150,0.4)" : "rgba(240,59,59,0.4)"}`,
              color: position?.side === "short" ? "#00c896" : "#f03b3b",
            }}>
              {position?.side === "short" ? "CLOSE SHORT" : "SELL SHORT"}
            </button>

            {/* Playback controls */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginTop: "auto" }}>
              {[
                { label: "⏮", action: () => { setCurrentBar(0); setIsPlaying(false); } },
                { label: isPlaying ? "⏸" : "▶", action: () => setIsPlaying(p => !p) },
                { label: "⏭", action: () => setCurrentBar(bars.length - 1) },
              ].map(({ label, action }) => (
                <button key={label} onClick={action} style={{
                  padding: "8px", fontSize: 14, cursor: "pointer", borderRadius: 4,
                  background: "#1a1d27", border: "1px solid #2a2d3a",
                  color: "#a0a5b4",
                }}>{label}</button>
              ))}
            </div>
            <button onClick={() => setCurrentBar(b => Math.min(b + 1, bars.length - 1))}
              style={{ padding: "7px", fontSize: 10, cursor: "pointer", borderRadius: 4, background: "#1a1d27", border: "1px solid #2a2d3a", color: "#7a7f8e" }}>
              +1 Bar
            </button>

            {/* Trade log */}
            {trades.length > 0 && (
              <div style={{ background: "#111318", border: "1px solid #1e2130", borderRadius: 4, padding: "6px 8px", maxHeight: 120, overflowY: "auto" }}>
                <div style={{ fontSize: 9, color: "#4a4f5e", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Trades</div>
                {trades.map((t, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span style={{ fontSize: 9, color: t.side === "buy" ? "#00c896" : "#f03b3b" }}>{t.side.toUpperCase()} {t.price.toFixed(2)}</span>
                    {t.pnl != null && <span style={{ fontSize: 9, fontFamily: "monospace", color: t.pnl >= 0 ? "#00c896" : "#f03b3b" }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(0)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
